import { useMemo } from 'react';
import { getFullName } from 'pos-client/components/scheduleBoard/utils/getFullName.ts';
import { trpc } from 'pos-client/utils/trpc.ts';
import type {
  LocationLinkedAssetModel,
  LocationLinkedAssetResultModel,
} from 'pos-shared/models/locationSchema.ts';

export enum AssetTypes {
  Technician = 'technician',
  Van = 'van',
}

export type LocationLinkedAssetWithFullNameModel = LocationLinkedAssetModel & {
  fullName: string;
};

// get assigned assets for a specific location
export const useLocationAssignedAssets = (
  locationId: string,
  shouldGetAllAssets = true
) => {
  const { data: assets, isFetching: isFetchingAssets } =
    trpc.locations.getAssetsForLocation.useQuery(
      { pageSize: '1000' },
      { enabled: shouldGetAllAssets }
    );

  const { data: linkedAssets, isFetching: isFetchingLinked } =
    trpc.locations.getLocationLinkedAssets.useQuery(
      {
        id: locationId,
      },
      { enabled: !!locationId }
    );

  const availableAssets = useMemo(() => {
    type Rec = { id: string; name: string };
    const ids: Rec[] = [];
    // Normalizing data structure
    if (linkedAssets?.results) {
      for (const i of linkedAssets.results) {
        for (const j of i.linkedAssets) {
          ids.push({ id: j.id, name: j.name });
        }
      }
    }
    // Filter out used linked assets
    if (assets) {
      return assets.filter((asset) => {
        return !ids.some((i) => i.id === asset.id);
      });
    } else {
      return [];
    }
  }, [assets, linkedAssets]);

  const formattedAssets = useMemo(() => {
    return availableAssets.map((asset) => ({
      label: asset.name,
      value: asset.id,
    }));
  }, [availableAssets]);

  const filteredAssets = useMemo(() => {
    const assets = linkedAssets?.results.filter(
      (entity: LocationLinkedAssetResultModel) =>
        entity.serviceLocationId === locationId
    );
    if (assets?.length) {
      return assets[0].linkedAssets;
    }
  }, [linkedAssets]);

  return {
    isLoading: isFetchingAssets || isFetchingLinked,
    isFetchingLinked,
    assets: availableAssets,
    dropDownAssets: formattedAssets,
    linkedAssets,
    filteredAssets,
  };
};

// used to get assigned location assets by a single asset type
export const useLocationAssignedAssetsByType = (
  locationId: string,
  assetTypeName: AssetTypes
) => {
  const { linkedAssets, isLoading } = useLocationAssignedAssets(
    locationId,
    false
  );
  const { data: assetTypes, isFetching: isLoadingTypes } =
    trpc.getAssetTypes.useQuery();
  const currentAssetType = assetTypes?.find(
    (type) => (type.name.toLowerCase() as AssetTypes) === assetTypeName
  );
  const assets = useMemo((): LocationLinkedAssetWithFullNameModel[] => {
    const assetsWithFullName = linkedAssets?.results[0]?.linkedAssets.map(
      (asset) => ({
        ...asset,
        fullName: getFullName(asset),
      })
    );
    return assetsWithFullName ?? [];
  }, [linkedAssets]);

  return {
    assets:
      assets.filter((asset) => asset.typeId === currentAssetType?.id) ?? [],
    isLoadingAssets: isLoading || isLoadingTypes,
  };
};
